/* eslint-disable @typescript-eslint/consistent-type-assertions */

import { isToday } from 'date-fns';
import { type ContextFrom, createMachine } from 'xstate';
import { assign } from 'xstate/lib/actions';
import {
  getRoundedPercentageOfValue,
  getRoundedValueOfPercentage,
} from '@sg/garnish';

import { ignoreTimezone } from '@order/utils';

import {
  adjustWantedTime,
  APPLE_PAY,
  getInitialWantedTime,
  getMaxTipAmount,
  GOOGLE_PAY,
  storeWantedTime,
} from '../utils';
import {
  type BagReward,
  type OrderingCart,
  type OrderingLedger,
  type OrderingMachineActions,
  type OrderingMachineContext,
  type OrderingMachineEvents,
  type OrderingMachineServices,
  type PaymentMethod,
  type WantedTime,
} from './ordering-machine.types';
import { orderingMachineLogger } from './ordering-machine.utils';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A state machine to manage the ordering flow on bag + checkout.
 */
export const createOrderingMachine = <LineItem, DeliveryOrderDetail>(
  params: CreateOrderingMachineParams,
) => {
  const { orderSubmissionDelay, wantedTimesPollingInterval } = params;

  return createMachine(
    {
      // eslint-disable-next-line @typescript-eslint/consistent-type-imports
      tsTypes: {} as import('./ordering-machine.typegen').Typegen0,
      predictableActionArguments: true,
      preserveActionOrder: true,
      id: 'ordering-state-machine',
      schema: {
        context: {} as OrderingMachineContext<LineItem, DeliveryOrderDetail>,
        events: {} as OrderingMachineEvents,
        services: {} as OrderingMachineServices<LineItem, DeliveryOrderDetail>,
        actions: {} as OrderingMachineActions<LineItem, DeliveryOrderDetail>,
      },
      context: getInitialContext<LineItem, DeliveryOrderDetail>(),
      type: 'parallel',
      states: {
        ordering: {
          initial: 'idle',
          states: {
            idle: {
              initial: 'waiting',
              states: {
                waiting: {
                  description: 'An empty state used for the initial setup.',
                },
                postOrder: {
                  id: 'post-order',
                  entry: [
                    'storeOrderPostSubmission',
                    'onPostOrder',
                    'navigatePostOrder',
                    'resetContext',
                  ],
                },
                postOrderDelayed: {
                  id: 'post-order-delayed',
                  entry: ['storeOrderPostSubmission'],
                  after: {
                    1500: {
                      actions: [
                        'onPostOrder',
                        'navigatePostOrder',
                        'resetContext',
                      ],
                    },
                  },
                },
                idle: {
                  id: 'idle',
                  description: 'An idle state that awaits the next event.',
                  on: {
                    FETCH_INITIAL_DATA: {
                      description: 'Fetches the initial data.',
                      target: '#fetching-initial-data',
                    },
                    ADD_UPSELL_TO_BAG: {
                      description: 'Adds an upsell to the bag.',
                      target: '#adding-upsell-to-bag',
                      actions: ['setUpsellBeingAdded'],
                    },
                    CHANGE_LINE_ITEM_QUANTITY: {
                      description: 'Changes a line item quantity.',
                      target: '#changing-line-item-quantity',
                      actions: ['setLineItemQuantity'],
                    },
                    CHANGE_UTENSILS_USAGE: {
                      description: 'Changes the utensils usage',
                      actions: ['changeUtensilsUsage'],
                    },
                    CHANGE_DROPOFF_LOCATION: {
                      description: 'Changes the dropoff location',
                      actions: ['changeDropoffLocation'],
                    },
                    SHOW_LOYALTY_INFO: {
                      description: 'Shows the loyalty availability info.',
                      target: '#loyalty-info',
                    },
                    SHOW_LOYALTY_TERMS: {
                      description: 'Shows the loyalty terms.',
                      actions: ['setRewardTermsBeingShown'],
                      target: '#loyalty-terms',
                    },
                    APPLY_REWARD: {
                      description: 'Applies a reward.',
                      actions: ['setRewardIdBeingUpdated'],
                      target: '#applying-reward',
                    },
                    REMOVE_REWARD: {
                      description: 'Removes a reward.',
                      actions: ['setRewardIdBeingUpdated'],
                      target: '#removing-reward',
                    },
                    APPLY_PROMO_CODE: {
                      description: 'Applies a promo code.',
                      target: '#applying-promo-code',
                    },
                    REQUEST_CHANNEL_LOCATION_CHANGE: {
                      description:
                        'Requests the location or channel to be changed',
                      actions: ['changeChannelOrLocation'],
                    },
                    REQUEST_TIME_CHANGE: [
                      {
                        target: '#changing-time',
                        cond: 'checkIfLoggedIn',
                      },
                      {
                        actions: ['requestLogin'],
                      },
                    ],
                    REQUEST_DELIVERY_PREFERENCES_CHANGE: {
                      description:
                        'Requests the delivery preferences to be changed',
                      target: '#changing-delivery-preferences',
                    },
                    REQUEST_CHECKOUT_LEDGER: {
                      description: 'Requests the checkout ledger',
                      target: '#checkout-ledger',
                    },
                  },
                },
                changingTime: {
                  id: 'changing-time',
                  on: {
                    CANCEL_REQUEST: '#idle',
                    CHANGE_TIME: [
                      {
                        description: 'Changes the time',
                        actions: ['changeTime', 'storeWantedTime'],
                        target: '#idle',
                        cond: 'checkIfDoesNotNeedsToShowFutureDateModal',
                      },
                      {
                        description:
                          'Changes the time and shows future date warning',
                        actions: ['changeTime', 'storeWantedTime'],
                        target: '#showing-future-date-warning',
                        cond: 'checkIfNeedsToShowFutureDateModal',
                      },
                    ],
                  },
                },
                changingDeliveryPreferences: {
                  id: 'changing-delivery-preferences',
                  on: {
                    CANCEL_REQUEST: '#idle',
                    CHANGE_DELIVERY_PREFERENCES: {
                      actions: ['changeDeliveryPreferences'],
                      target: '#idle',
                    },
                  },
                },
                showingFutureDateWarning: {
                  id: 'showing-future-date-warning',
                  on: {
                    CANCEL_REQUEST: '#idle',
                  },
                },
                showingRewardWarning: {
                  id: 'showing-reward-warning',
                  on: {
                    CANCEL_REQUEST: '#idle',
                  },
                },
                checkoutLedger: {
                  id: 'checkout-ledger',
                  on: {
                    CANCEL_REQUEST: '#idle',
                    CHANGE_TIP: { actions: ['changeTip'] },
                    REQUEST_PAYMENT_METHOD_CHANGE: '#changing-payment-method',
                    REQUEST_PAYMENT_METHOD_ADD: '#payment-method-form',
                    CHANGE_CREDIT_USAGE: { actions: ['changeCreditUsage'] },
                    SUBMIT_ORDER: {
                      target: '#submitting-order',
                      actions: ['changePaymentMethod'],
                    },
                    SHOW_ORDER_PLAYBACK: {
                      target: '#order-playback',
                      actions: ['changePaymentMethod'],
                    },
                  },
                },
                postOrderFailureWithNextDayOrderNotice: {
                  id: 'post-order-failure-with-next-day-order-notice',
                  after: {
                    100: '#next-day-order-notice',
                  },
                  on: {
                    CANCEL_REQUEST: '#idle',
                  },
                },
                timeslotAdjustedNotice: {
                  id: 'timeslot-adjusted-notice',
                  on: {
                    CANCEL_REQUEST: '#idle',
                    REQUEST_TIME_CHANGE: '#changing-time',
                    SUBMIT_ORDER: '#submitting-order',
                  },
                },
                nextDayOrderNotice: {
                  id: 'next-day-order-notice',
                  on: {
                    CANCEL_REQUEST: '#idle',
                  },
                },
                orderPlayback: {
                  id: 'order-playback',
                  after: {
                    [orderSubmissionDelay]: {
                      target: '#submitting-order',
                    },
                  },
                  on: {
                    CANCEL_REQUEST: {
                      target: '#idle',
                      actions: ['incrementPurchaseAttempt'],
                    },
                    SUBMIT_ORDER: {
                      target: '#submitting-order',
                      actions: ['changePaymentMethod'],
                    },
                  },
                },
                changingPaymentMethod: {
                  id: 'changing-payment-method',
                  on: {
                    CANCEL_REQUEST: '#idle',
                    REQUEST_CHECKOUT_LEDGER: '#checkout-ledger',
                    REQUEST_GIFT_CARD_FORM: '#gift-card-form',
                    REQUEST_PAYMENT_METHOD_ADD: '#payment-method-form',
                    FINISH_PAYMENT_SELECTION: '#checkout-ledger',
                    CHANGE_PAYMENT_METHOD: {
                      description: 'Changes the payment method',
                      actions: ['changePaymentMethod'],
                    },
                  },
                },
                paymentMethodForm: {
                  id: 'payment-method-form',
                  on: {
                    CANCEL_REQUEST: '#idle',
                    REQUEST_PAYMENT_METHOD_CHANGE: '#changing-payment-method',
                    ADD_PAYMENT_METHOD: '#adding-payment-method',
                  },
                },
                giftCardForm: {
                  id: 'gift-card-form',
                  on: {
                    CANCEL_REQUEST: '#idle',
                    REQUEST_PAYMENT_METHOD_CHANGE: '#changing-payment-method',
                    APPLY_GIFT_CARD: '#applying-gift-card',
                  },
                },
                loyaltyInfo: {
                  id: 'loyalty-info',
                  on: {
                    CANCEL_REQUEST: '#idle',
                  },
                },
                loyaltyTerms: {
                  id: 'loyalty-terms',
                  on: {
                    CANCEL_REQUEST: '#idle',
                  },
                },
              },
            },
            loading: {
              states: {
                fetchingInitialData: {
                  id: 'fetching-initial-data',
                  initial: 'fetching',
                  states: {
                    fetching: {
                      description: `A state for fetching the initial data (cart + rewards).`,
                      invoke: {
                        src: 'fetchInitialData',
                        onDone: {
                          target: '#retrieving-initial-wanted-time',
                          actions: ['onFetchInitialDataDone', 'onBagReady'],
                        },
                      },
                    },
                  },
                },
                retrievingInitialWantedTime: {
                  id: 'retrieving-initial-wanted-time',
                  initial: 'retrieving',
                  states: {
                    retrieving: {
                      description:
                        'A state for retrieving the initial wanted time from local storage, if it exists.',
                      invoke: {
                        src: 'getInitialWantedTime',
                        onDone: {
                          target: '#idle',
                          actions: ['onGetInitialWantedTimeDone'],
                        },
                        onError: '#idle',
                      },
                    },
                  },
                },
                addingUpsellToBag: {
                  id: 'adding-upsell-to-bag',
                  initial: 'adding',
                  states: {
                    adding: {
                      description: 'A state for adding an upsell to the bag.',
                      invoke: {
                        src: 'addUpsellToBag',
                        onDone: {
                          target: ['#idle', '#preparing-to-fetch-rewards'],
                          actions: ['onAddUpsellToBagDone'],
                        },
                      },
                    },
                  },
                },
                changingLineItemQuantity: {
                  id: 'changing-line-item-quantity',
                  initial: 'changing',
                  states: {
                    changing: {
                      description:
                        'A state for changing the line item quantity.',
                      invoke: {
                        src: 'updateLineItem',
                        onDone: {
                          target: ['#idle', '#preparing-to-fetch-rewards'],
                          actions: ['onUpdateLineItemDone'],
                        },
                      },
                      on: {
                        CHANGE_LINE_ITEM_QUANTITY: {
                          description:
                            'Changes another line item quantity while the present one is being changed.',
                          target: '#changing-line-item-quantity',
                          actions: ['setLineItemQuantity'],
                        },
                      },
                    },
                  },
                },
                applyingReward: {
                  id: 'applying-reward',
                  initial: 'fetching',
                  states: {
                    fetching: {
                      description: 'A state for applying the reward.',
                      invoke: {
                        src: 'applyReward',
                        onDone: [
                          {
                            target: ['#idle', '#fetching-rewards'],
                            actions: ['onApplyRewardDone'],
                            cond: (_context, event) => 'cart' in event.data,
                          },
                          {
                            target: '#showing-reward-warning',
                            actions: ['onRewardError'],
                            cond: (_context, event) => 'error' in event.data,
                          },
                        ],
                      },
                    },
                  },
                },
                removingReward: {
                  id: 'removing-reward',
                  initial: 'fetching',
                  states: {
                    fetching: {
                      description: 'A state for removing the reward.',
                      invoke: {
                        src: 'removeReward',
                        onDone: [
                          {
                            target: ['#idle', '#fetching-rewards'],
                            actions: ['onRemoveRewardDone'],
                            cond: (_context, event) => 'cart' in event.data,
                          },
                          {
                            target: '#showing-reward-warning',
                            actions: ['onRewardError'],
                            cond: (_context, event) => 'error' in event.data,
                          },
                        ],
                      },
                    },
                  },
                },
                applyingPromoCode: {
                  id: 'applying-promo-code',
                  initial: 'applying',
                  states: {
                    applying: {
                      description: 'A state for applying the promo code.',
                      invoke: {
                        src: 'applyPromoCode',
                        onDone: [
                          {
                            target: '#idle',
                            actions: ['onApplyPromoCodeDone'],
                            cond: (_context, event) => event.data.success,
                          },
                          {
                            target: '#showing-reward-warning',
                            actions: ['onRewardError'],
                            cond: (_context, event) => !event.data.success,
                          },
                        ],
                      },
                    },
                  },
                },
                addingPaymentMethod: {
                  id: 'adding-payment-method',
                  initial: 'adding',
                  states: {
                    adding: {
                      description: 'A state for adding a payment method.',
                      invoke: {
                        src: 'addPaymentMethod',
                        onDone: [
                          {
                            target: '#checkout-ledger',
                            actions: ['onAddPaymentMethodDone'],
                            cond: 'checkIfPaymentMethodAdded',
                          },
                          {
                            target: '#payment-method-form',
                            cond: 'checkIfPaymentMethodNotAdded',
                          },
                        ],
                      },
                    },
                  },
                },
                applyingGiftCard: {
                  id: 'applying-gift-card',
                  initial: 'applying',
                  states: {
                    applying: {
                      description: 'A state for applying a gift card.',
                      invoke: {
                        src: 'applyGiftCard',
                        onDone: [
                          {
                            target: '#changing-payment-method',
                            actions: ['onApplyGiftCardDone'],
                            cond: 'checkIfGiftCardApplied',
                          },
                          {
                            target: '#gift-card-form',
                            cond: 'checkIfGiftCardNotApplied',
                          },
                        ],
                      },
                    },
                  },
                },
                submittingOrder: {
                  id: 'submitting-order',
                  initial: 'submitting',
                  states: {
                    submitting: {
                      description: `A state for submitting the order`,
                      on: { CANCEL_REQUEST: '#idle' },
                      invoke: {
                        src: 'submitOrder',
                        onDone: [
                          {
                            target: '#post-order-delayed',
                            cond: 'checkIfOrderSubmissionSuccessWithDelayedPostOrder',
                          },
                          {
                            target: '#post-order',
                            cond: 'checkIfOrderSubmissionSuccess',
                          },
                          {
                            target:
                              '#post-order-failure-with-next-day-order-notice',
                            actions: [
                              'handleTimeslotUnavailable',
                              'incrementPurchaseAttempt',
                            ],
                            cond: 'checkIfWillBeFutureOrder',
                          },
                          {
                            target: '#timeslot-adjusted-notice',
                            actions: [
                              'handleTimeslotUnavailable',
                              'incrementPurchaseAttempt',
                            ],
                            cond: 'checkIfTimeslotIsUnavailableAndShouldShowModal',
                          },
                          {
                            target: '#idle',
                            actions: [
                              'handleTimeslotUnavailable',
                              'incrementPurchaseAttempt',
                            ],
                            cond: 'checkIfTimeslotIsUnavailable',
                          },
                          {
                            target: '#idle',
                            actions: ['incrementPurchaseAttempt'],
                            cond: 'checkIfGenericOrderSubmissionError',
                          },
                        ],
                      },
                    },
                  },
                },
              },
            },
          },
        },
        wantedTimes: {
          initial: 'waiting',
          states: {
            polling: {
              invoke: {
                src: 'fetchWantedTimes',
                onDone: {
                  target: 'waiting',
                  actions: ['updateWantedTimes'],
                  cond: 'checkIfHasWantedTimes',
                },
              },
            },
            waiting: {
              on: { REFETCH_WANTED_TIMES: 'polling' },
              after: {
                [wantedTimesPollingInterval]: {
                  target: 'polling',
                },
              },
            },
          },
        },
        lineItems: {
          initial: 'idle',
          states: {
            idle: {
              id: 'line-items-idle',
              on: {
                START_CHANGING_LINE_ITEM_QUANTITY: {
                  description:
                    'When the customer taps a quantity stepper button but the request as not started yet, we still need to block proceeding to checkout.',
                  target: '#line-items-pending-quantity-change',
                },
              },
            },
            pending: {
              id: 'line-items-pending-quantity-change',
              on: {
                STOP_CHANGING_LINE_ITEM_QUANTITY: {
                  description:
                    'When the customer cancels the quantity stepper change by returning to the original quantity, or when the quantity change request starts.',
                  target: '#line-items-idle',
                },
              },
            },
          },
        },
        rewards: {
          initial: 'idle',
          states: {
            idle: {
              id: 'rewards-idle',
              on: {
                ASSIGN_REWARDS: {
                  actions: ['updateRewards'],
                },
                REWARDS_FETCH_ERROR: {
                  target: '#rewards-error',
                  actions: ['clearRewards', 'onFetchRewardsError'],
                },
              },
            },
            error: {
              id: 'rewards-error',
              on: {
                REFETCH_REWARDS: '#preparing-to-fetch-rewards',
              },
            },
            preparingToFetchRewards: {
              id: 'preparing-to-fetch-rewards',
              always: [
                {
                  target: '#fetching-rewards-deferred',
                  cond: 'checkIfShouldDeferRewardsFetching',
                },
                {
                  target: '#fetching-rewards',
                  cond: 'checkIfLoggedIn',
                },
                {
                  target: '#rewards-idle',
                },
              ],
            },
            fetchingRewards: {
              id: 'fetching-rewards',
              invoke: {
                src: 'fetchRewards',
                onDone: {
                  target: '#rewards-idle',
                  actions: ['updateRewards'],
                },
                onError: {
                  target: '#rewards-error',
                  actions: ['onFetchRewardsError'],
                },
              },
            },
            fetchingRewardsDeferred: {
              id: 'fetching-rewards-deferred',
              description:
                'Deferred version of the `fetchingRewards` state that allows to change the context and state using external events rather than an inline service.',
              on: {
                ASSIGN_REWARDS: {
                  target: '#rewards-idle',
                  actions: ['updateRewards'],
                },
                REWARDS_FETCH_ERROR: {
                  target: '#rewards-error',
                  actions: ['clearRewards', 'onFetchRewardsError'],
                },
              },
            },
          },
        },
        stripe: {
          initial: 'idle',
          states: {
            idle: {
              always: [
                {
                  target: 'initializing',
                  cond: 'checkIfExpressPaymentIsEnabled',
                },
                {
                  target: 'ready',
                },
              ],
            },
            initializing: {
              on: {
                SET_AVAILABLE_EXPRESS_PAYMENTS: {
                  target: 'ready',
                  actions: ['setAvailableExpressPayments'],
                },
              },
              always: [
                {
                  target: 'ready',
                  cond: 'checkIfLoggedOut',
                },
              ],
              after: {
                5000: 'ready',
              },
            },
            ready: {
              always: [
                {
                  actions: ['setPaymentMethodIdAsApplePay'],
                  cond: 'checkShouldSetPaymentMethodIdAsApplePay',
                },
                {
                  actions: ['setPaymentMethodIdAsGooglePay'],
                  cond: 'checkShouldSetPaymentMethodIdAsGooglePay',
                },
                {
                  actions: ['setPaymentMethodIdAsDefaultPaymentMethod'],
                  cond: 'checkShouldSetPaymentMethodIdAsDefaultPaymentMethod',
                },
                {
                  actions: ['setPaymentMethodIdAsFirstPaymentMethod'],
                  cond: 'checkShouldSetPaymentMethodIdAsFirstPaymentMethod',
                },
              ],
            },
          },
        },
      },
      on: {
        START: {
          description:
            'Starts/restarts the machine by fetching the initial data and clearing the state.',
          target: ['#fetching-initial-data', '#preparing-to-fetch-rewards'],
          actions: ['resetContext'],
        },
      },
    },
    {
      actions: {
        /**
         * Stores the resolved initial data in the context.
         */
        onFetchInitialDataDone: assign({
          isInitialDataReady: true,
          isWantedTimeAsap: true,

          cart: (_context, event) => event.data.cart,
          paymentMethods: (_context, event) => event.data.paymentMethods,
          defaultPaymentMethodId: (_context, event) =>
            event.data.defaultPaymentMethodId,
          hasDefaultPaymentMethodCard: (_context, event) =>
            event.data.paymentMethods.some(
              (pm) => pm.id === event.data.defaultPaymentMethodId,
            ),
          selectedTip: (_context, event) => event.data.selectedTip,
          availableTips: (_context, event) => event.data.availableTips,
          availablePoints: (_context, event) => event.data.availablePoints,
          availableCredit: (_context, event) => event.data.availableCredit,
          giftCardBalance: (_context, event) => event.data.giftCardBalance,
          customer: (_context, event) => event.data,
          wantedTime: (_context, event) => event.data.cart.wantedTimes[0],
          dropoffLocation: (_context, event) =>
            event.data.cart.dropoffLocations[0]?.value,
          appliedRewardId(_context, event) {
            return event.data.cart.appliedRewardId;
          },
          stripeCustomerId: (_context, event) => event.data.stripeCustomerId,
          stripeCustomerKey: (_context, event) => event.data.stripeCustomerKey,
          stripeSession: (_context, event) => event.data.stripeSession,
        }),

        onGetInitialWantedTimeDone: assign({
          wantedTime: (_context, event) => event.data.wantedTime,
          isWantedTimeAsap: (context, event) =>
            context.cart.wantedTimes[0] === event.data.wantedTime,
        }),

        /**
         * Stores the rewards that were returned.
         */
        updateRewards: assign({
          rewards: (_context, event) => event.data.rewards,
          appliedRewardId(context, event) {
            if (!context.appliedRewardId) return undefined;

            // discard the applied reward if it's no longer present.
            return event.data.rewards.find(
              (reward) => reward.id === context.appliedRewardId,
            )?.id;
          },
        }),

        /**
         * Clears rewards.
         */
        clearRewards: assign({
          rewards: [],
          appliedRewardId: undefined,
        }),

        /**
         * Logs errors with fetching rewards.
         */
        onFetchRewardsError() {
          orderingMachineLogger.error('Failed to fetch rewards');
        },

        /**
         * Stores which upsell is being added.
         */
        setUpsellBeingAdded: assign({
          lineItemIdsBeingUpdated: (context, event) => [
            ...context.lineItemIdsBeingUpdated,
            event.params.productId,
          ],
        }),

        /**
         * Stores which line item quantity is being changed.
         */
        setLineItemQuantity: assign({
          lineItemIdsBeingUpdated: (context, event) => [
            ...context.lineItemIdsBeingUpdated,
            event.params.lineItemId,
          ],
        }),

        /**
         * Updates the resolved cart in the context.
         */
        onAddUpsellToBagDone: assign({
          lineItemIdsBeingUpdated: [],
          cart(context, event) {
            if ('cart' in event.data) {
              return event.data.cart;
            }

            return context.cart;
          },
        }),

        /**
         * Updates the resolved cart in the context.
         * It also updates the tip to be the same percentage, but of the new subtotal.
         */
        onUpdateLineItemDone: assign({
          selectedTip: (context, event) =>
            getNewTip(
              context.selectedTip,
              context.cart.maxTipAmount,
              context.availableTips,
              event.data?.ledger,
            ),
          cart: (context, event) => event?.data ?? context.cart,
          appliedRewardId(_context, event) {
            return event.data?.appliedRewardId;
          },
          lineItemIdsBeingUpdated: [],
        }),

        /**
         * Stores which reward is being updated.
         */
        setRewardIdBeingUpdated: assign({
          rewardIdBeingUpdated: (_context, event) => event.params.rewardId,
        }),

        /**
         * Stores which terms are being shown.
         */
        setRewardTermsBeingShown: assign({
          rewardTermsBeingShown: (_context, event) => event.params.rewardId,
        }),

        /**
         * Stores the updated cart in the context.
         */
        onApplyRewardDone: assign({
          rewardIdBeingUpdated: undefined,
          appliedRewardId(_context, event) {
            if ('cart' in event.data) {
              return event.data?.cart?.appliedRewardId;
            }
          },
          cart(context, event) {
            if ('cart' in event.data) {
              return event.data.cart;
            }

            return context.cart;
          },
          selectedTip: (context, event) => {
            if ('cart' in event.data) {
              return getNewTip(
                context.selectedTip,
                context.cart.maxTipAmount,
                context.availableTips,
                event.data?.cart?.ledger,
              );
            }

            return context.selectedTip;
          },
        }),

        /**
         * Stores the updated cart in the context.
         */
        onRemoveRewardDone: assign({
          rewardIdBeingUpdated: undefined,
          appliedRewardId: undefined,
          cart(context, event) {
            if ('cart' in event.data) {
              return event.data.cart;
            }

            return context.cart;
          },
          selectedTip: (context, event) => {
            if ('cart' in event.data) {
              return getNewTip(
                context.selectedTip,
                context.cart.maxTipAmount,
                context.availableTips,
                event.data?.cart?.ledger,
              );
            }

            return context.selectedTip;
          },
        }),

        /**
         * Stores the reward error in the context.
         */
        onRewardError: assign({
          rewardIdBeingUpdated: undefined,
          rewardError(_context, event) {
            if ('error' in event.data) {
              return event.data.error;
            }
          },
          isRewardMissingSku(_context, event) {
            if ('isRewardMissingSku' in event.data) {
              return Boolean(event.data.isRewardMissingSku);
            }

            return false;
          },
        }),

        /**
         * Stores the resulting rewards post promo code in the context.
         */
        onApplyPromoCodeDone: assign({
          rewards: (_context, event) => event.data.rewards,
        }),

        /**
         * Stores the resulting gift card balance and available credit in the context.
         */
        onApplyGiftCardDone: assign({
          giftCardBalance: (context, event) =>
            event.data?.giftCardBalance ?? context.giftCardBalance,
          availableCredit: (context, event) =>
            event.data?.availableCredit ?? context.availableCredit,
        }),

        /**
         * Stores the resulting payment method in the context.
         */
        onAddPaymentMethodDone: assign({
          paymentMethodId: (_context, event) => event.data?.id,
          paymentMethods: (context, event) => {
            if ('data' in event && event.data?.id) {
              return [event.data, ...context.paymentMethods];
            }

            return context.paymentMethods;
          },
        }),

        /**
         * Stores the available express payments for this device.
         */
        setAvailableExpressPayments: assign({
          expressPayments: (_context, event) => ({
            isExpressPaymentsEnabled: true,
            isStripeReady: true,
            canApplePay: event.params.canApplePay,
            canGooglePay: event.params.canGooglePay,
          }),
        }),

        /**
         * Set the selected payment method id as Apple Pay.
         */
        setPaymentMethodIdAsApplePay: assign({
          paymentMethodId: APPLE_PAY,
        }),

        /**
         * Set the selected payment method id as Google Pay.
         */
        setPaymentMethodIdAsGooglePay: assign({
          paymentMethodId: GOOGLE_PAY,
        }),

        /**
         * Set the selected payment method id as the default payment method.
         */
        setPaymentMethodIdAsDefaultPaymentMethod: assign({
          paymentMethodId(context) {
            const { defaultPaymentMethodId } = context;
            return defaultPaymentMethodId;
          },
        }),

        /**
         * Set the selected payment method id as the first available payment method.
         */
        setPaymentMethodIdAsFirstPaymentMethod: assign({
          paymentMethodId(context) {
            const { paymentMethods } = context;
            const firstAvailablePaymentMethod = paymentMethods?.[0]?.id;
            return firstAvailablePaymentMethod;
          },
        }),

        /**
         * Resets the context of the machine.
         *
         * Can be used to clear the state before restarting the machine.
         */
        resetContext: assign(
          getInitialContextWithoutFlags<LineItem, DeliveryOrderDetail>(),
        ),

        /**
         * Changes the channel or location.
         */
        changeChannelOrLocation(_context) {
          orderingMachineLogger.debug(
            'Please provide `changeChannelOrLocation` action.',
          );
        },

        /**
         * Changes the time.
         */
        changeTime: assign({
          wantedTime: (_context, event) => event.params.wantedTime,
          isWantedTimeAsap: (context, event) =>
            context.cart.wantedTimes[0] === event.params.wantedTime,
          hasShownFutureDateWarning: (context, event) =>
            event.params.isFutureDate || context.hasShownFutureDateWarning,
        }),

        async storeWantedTime(context, event) {
          if (!context?.cart?.orderId || !event?.params?.wantedTime?.time) {
            return;
          }

          await storeWantedTime(
            context.cart.orderId,
            event.params.wantedTime.time,
          );
        },

        /**
         * Changes the delivery preferences.
         */
        changeDeliveryPreferences: assign({
          cart: (context, event) => ({
            ...context.cart,
            deliveryPreferences: event.params,
          }),
        }),

        /**
         * Updates the wanted times.
         */
        updateWantedTimes: assign({
          cart: (context, event) => ({
            ...context.cart,
            wantedTimes: event.data.wantedTimes,
          }),
          wantedTime(context, event) {
            const firstAvailableTimeslot = event.data.wantedTimes[0];

            // Do not overwrite the selected timeslot with the first one.
            if (!context.isWantedTimeAsap) {
              return context.wantedTime;
            }

            // Do not automatically bump the order to a day in the future.
            if (!isWantedTimeToday(firstAvailableTimeslot)) {
              return context.wantedTime;
            }

            return firstAvailableTimeslot;
          },
        }),

        /**
         * Changes the tip.
         */
        changeTip: assign({
          selectedTip: (_context, event) => event.params.tip,
        }),

        /**
         * Changes the dropoff location.
         */
        changeDropoffLocation: assign({
          dropoffLocation: (_context, event) => event.params.dropoffLocation,
        }),

        /**
         * Changes the payment method id for this order.
         */
        changePaymentMethod: assign({
          paymentMethodId: (context, event) =>
            event.params.paymentMethodId || context.paymentMethodId,
        }),

        /**
         * Changes whether or not this order should use credit.
         */
        changeCreditUsage: assign({
          shouldUseCredit: (_context, event) => event.params.shouldUseCredit,
        }),

        /**
         * Changes the payment method id and whether credit is used.
         */
        changeUtensilsUsage: assign({
          shouldIncludeUtensils: (_context, event) =>
            event.params.shouldIncludeUtensils,
        }),

        /**
         * If the order fails due to timeslot unavailable, select the next available time.
         * Also removes the failed timeslot from the available times.
         */
        handleTimeslotUnavailable: assign({
          cart: (context) => ({
            ...context.cart,
            wantedTimes: context.cart.wantedTimes.filter(
              (wantedTime) => wantedTime !== context.wantedTime,
            ),
          }),
          previousWantedTime: (context) => context.wantedTime,
          wantedTime(context) {
            const nextTimeslot = context.cart.wantedTimes.find(
              (wantedTime) => wantedTime !== context.wantedTime,
            );

            // Do not automatically bump the order to a day in the future.
            if (!isWantedTimeToday(nextTimeslot)) {
              return context.wantedTime;
            }

            return nextTimeslot;
          },
          isWantedTimeAsap: true,
        }),

        /**
         * Redirects the customer to the login screen.
         */
        requestLogin(_context) {
          orderingMachineLogger.debug('Please provide `requestLogin` action.');
        },

        /**
         * Stores the order post submission in the context for delayed post-order actions.
         */
        storeOrderPostSubmission: assign({
          orderId: (_context, event) => {
            if ('data' in event && event.data?.order?.id) {
              return event.data.order.id;
            }

            return undefined;
          },
          hasEarnedLoyaltyPoints: (_context, event) => {
            if (
              'data' in event &&
              event.data?.order?.hasEarnedLoyaltyPoints !== undefined
            ) {
              return event.data.order.hasEarnedLoyaltyPoints;
            }

            return undefined;
          },
        }),

        // Used to remount the Stripe element after failures.
        incrementPurchaseAttempt: assign({
          purchaseAttempt: (context) => context.purchaseAttempt + 1,
        }),

        async onPostOrder(_context) {
          orderingMachineLogger.debug('Please provide `onPostOrder` action.');

          return undefined;
        },
      },
      services: {
        async fetchInitialData(_context) {
          orderingMachineLogger.debug('Please provide `fetchCart` service.');

          return {
            cart: {} as OrderingCart<LineItem, DeliveryOrderDetail>,
            paymentMethods: [] as readonly PaymentMethod[],
            defaultPaymentMethodId: undefined,
            hasDefaultPaymentMethodCard: false,
            selectedTip: 0,
            availableTips: [],
            availablePoints: 0,
            availableCredit: 0,
            giftCardBalance: 0,
            customerId: '',
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            stripeCustomerId: '',
            stripeCustomerKey: '',
            stripeSession: '',
            isNewCustomer: true,
            isOptedOutFromLoyalty: false,
          };
        },
        async getInitialWantedTime(context) {
          return {
            wantedTime: await getInitialWantedTime(
              context.cart.orderId,
              context.cart.wantedTimes,
            ),
          };
        },
        async fetchRewards(_context) {
          orderingMachineLogger.debug('Please provide `fetchRewards` service.');

          return {
            rewards: [] as readonly BagReward[],
          };
        },
        async fetchWantedTimes(_context) {
          orderingMachineLogger.debug(
            'Please provide `fetchWantedTimes` service.',
          );

          return {
            wantedTimes: [],
          };
        },
        async addUpsellToBag(_context) {
          orderingMachineLogger.debug(
            'Please provide `addUpsellToBag` service.',
          );

          return { cart: {} as OrderingCart<LineItem, DeliveryOrderDetail> };
        },
        async updateLineItem(_context) {
          orderingMachineLogger.debug(
            'Please provide `updateLineItem` service.',
          );

          return {} as OrderingCart<LineItem, DeliveryOrderDetail>;
        },
        async applyReward(_context) {
          orderingMachineLogger.debug('Please provide `applyReward` service.');

          return { cart: {} as OrderingCart<LineItem, DeliveryOrderDetail> };
        },
        async removeReward(_context) {
          orderingMachineLogger.debug('Please provide `removeReward` service.');

          return { cart: {} as OrderingCart<LineItem, DeliveryOrderDetail> };
        },
        async applyPromoCode(_context) {
          orderingMachineLogger.debug(
            'Please provide `applyPromoCode` service.',
          );

          return {
            rewards: [],
            success: false,
            error: undefined,
            isRewardMissingSku: false,
          };
        },
        async applyGiftCard(_context) {
          orderingMachineLogger.debug(
            'Please provide `applyGiftCard` service.',
          );

          return null;
        },
        async addPaymentMethod(_context) {
          orderingMachineLogger.debug(
            'Please provide `addPaymentMethod` service.',
          );

          return null;
        },
        async submitOrder(_context) {
          orderingMachineLogger.debug('Please provide `submitOrder` service.');

          return {
            success: false,
            isTimeslotUnavailable: false,
            error: undefined,
          };
        },
      },
      guards: {
        checkIfDoesNotNeedsToShowFutureDateModal: (context, event) =>
          !event.params.isFutureDate || context.hasShownFutureDateWarning,
        checkIfNeedsToShowFutureDateModal: (context, event) =>
          event.params.isFutureDate && !context.hasShownFutureDateWarning,
        checkIfOrderSubmissionSuccessWithDelayedPostOrder: (context, event) =>
          event.data.success && context.isOrderPlaybackEnabled,
        checkIfOrderSubmissionSuccess: (_context, event) => event.data.success,
        checkIfTimeslotIsUnavailable: (_context, event) =>
          !event.data.success && event.data.isTimeslotUnavailable,
        checkIfTimeslotIsUnavailableAndShouldShowModal: (context, event) =>
          !event.data.success &&
          event.data.isTimeslotUnavailable &&
          context.isTimeslotUnavailableModalEnabled,
        checkIfWillBeFutureOrder(context, event) {
          if (event.data.success || !event.data.isTimeslotUnavailable) {
            return false;
          }

          const nextTimeslot = context.cart.wantedTimes.find(
            (wantedTime) => wantedTime !== context.wantedTime,
          );

          return !isWantedTimeToday(nextTimeslot);
        },
        checkIfGenericOrderSubmissionError: (_context, event) =>
          !event.data.success && !event.data.isTimeslotUnavailable,
        checkIfGiftCardApplied: (_context, event) =>
          Boolean(event.data?.giftCardBalance),
        checkIfGiftCardNotApplied: (_context, event) =>
          !event.data?.giftCardBalance,
        checkIfPaymentMethodAdded: (_context, event) => Boolean(event.data?.id),
        checkIfPaymentMethodNotAdded: (_context, event) => !event.data?.id,
        checkIfHasWantedTimes: (_context, event) =>
          event.data.wantedTimes.length > 0,

        checkIfLoggedIn() {
          orderingMachineLogger.debug(
            'Please provide `checkIfLoggedIn` guard.',
          );

          return false;
        },

        checkIfLoggedOut() {
          orderingMachineLogger.debug(
            'Please provide `checkIfLoggedOut` guard.',
          );

          return false;
        },

        checkIfExpressPaymentIsEnabled(context) {
          return context.expressPayments.isExpressPaymentsEnabled;
        },

        /**
         * For Apple Pay to be the initial payment method id, the following must be true:
         * - The device supports Apple Pay.
         * - One of:
         *   - The default payment method id stored in payments-db is Apple Pay.
         *   - There is no default payment method id.
         *   - The default payment method id is for a credit card that was deleted.
         */
        checkShouldSetPaymentMethodIdAsApplePay(context) {
          const {
            expressPayments: { canApplePay },
            defaultPaymentMethodId,
            hasDefaultPaymentMethodCard,
          } = context;

          // Skipping the payment method selection if it's already selected or not ready yet.
          if (shouldSkipPaymentMethodAssignment(context)) return false;

          // Device must support Apple Pay.
          if (!canApplePay) return false;

          // If the default is Apple Pay, use it.
          if (defaultPaymentMethodId === APPLE_PAY) return true;

          // If there is no default, use it.
          if (!defaultPaymentMethodId) return true;

          // If the default is a deleted card, use it.
          if (!hasDefaultPaymentMethodCard) return true;

          return false;
        },

        /**
         * For Google Pay to be the initial payment method id, the following must be true:
         * - The device supports Google Pay.
         * - One of:
         *   - The default payment method id stored in payments-db is Google Pay.
         *   - There is no default payment method id.
         *   - The default payment method id is for a credit card that was deleted.
         */
        checkShouldSetPaymentMethodIdAsGooglePay(context) {
          const {
            expressPayments: { canGooglePay },
            defaultPaymentMethodId,
            hasDefaultPaymentMethodCard,
          } = context;

          // Skipping the payment method selection if it's already selected or not ready yet.
          if (shouldSkipPaymentMethodAssignment(context)) return false;

          // Device must support Google Pay.
          if (!canGooglePay) return false;

          // If the default is Google Pay, use it.
          if (defaultPaymentMethodId === GOOGLE_PAY) return true;

          // If there is no default, use it.
          if (!defaultPaymentMethodId) return true;

          // If the default is a deleted card, use it.
          if (!hasDefaultPaymentMethodCard) return true;

          return false;
        },

        /**
         * For the default payment method id to be the initial payment method id, the following must be true:
         * - It must be for a credit card that is still valid.
         */
        checkShouldSetPaymentMethodIdAsDefaultPaymentMethod(context) {
          const { hasDefaultPaymentMethodCard } = context;

          // Skipping the payment method selection if it's already selected or not ready yet.
          if (shouldSkipPaymentMethodAssignment(context)) return false;

          // If the default is an existing card, use it.
          if (hasDefaultPaymentMethodCard) return true;

          return false;
        },

        /**
         * For the first payment method in the list to be the initial payment method id, the following must be true:
         * - There is no default payment method id or it is for a deleted card.
         * - Neither express checkout option is supported by this device.
         */
        checkShouldSetPaymentMethodIdAsFirstPaymentMethod(context) {
          const {
            expressPayments: { canApplePay, canGooglePay },
            paymentMethods,
            hasDefaultPaymentMethodCard,
          } = context;

          // Skipping the payment method selection if it's already selected or not ready yet.
          if (shouldSkipPaymentMethodAssignment(context)) return false;

          // Must have payment methods available.
          if (paymentMethods.length === 0) return false;

          // Express checkout options have priority.
          if (canApplePay || canGooglePay) return false;

          // Default cards have priority.
          if (hasDefaultPaymentMethodCard) return false;

          return true;
        },
      },
    },
  );
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Skipping the payment method selection if the initial data isn't ready or the payment method id was already set.
 */
const shouldSkipPaymentMethodAssignment = (context: {
  isInitialDataReady: boolean;
  paymentMethodId: string | undefined;
}) => {
  return !context.isInitialDataReady || Boolean(context.paymentMethodId);
};

const getInitialContext = <
  LineItem,
  DeliveryOrderDetail,
>(): OrderingMachineContext<LineItem, DeliveryOrderDetail> => {
  return {
    ...getInitialContextWithoutFlags(),
    isOrderPlaybackEnabled: false,
    isTimeslotUnavailableModalEnabled: false,
    expressPayments: {
      isExpressPaymentsEnabled: false,
      isStripeReady: false,
      canApplePay: false,
      canGooglePay: false,
    },
  };
};

const getInitialContextWithoutFlags = <LineItem, DeliveryOrderDetail>(): Omit<
  OrderingMachineContext<LineItem, DeliveryOrderDetail>,
  | 'expressPayments'
  | 'isOrderPlaybackEnabled'
  | 'isTimeslotUnavailableModalEnabled'
> => {
  return {
    isInitialDataReady: false,
    cart: {} as OrderingCart<LineItem, DeliveryOrderDetail>,
    rewards: [],
    rewardError: undefined,
    isRewardMissingSku: false,
    appliedRewardId: undefined,
    paymentMethods: [],
    defaultPaymentMethodId: undefined,
    hasDefaultPaymentMethodCard: false,
    availablePoints: 0,
    availableCredit: 0,
    giftCardBalance: 0,

    purchaseAttempt: 0,
    stripeCustomerId: '',
    stripeCustomerKey: '',
    stripeSession: '',

    customer: {
      customerId: '',
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      isNewCustomer: true,
      isOptedOutFromLoyalty: false,
    },

    wantedTime: undefined,
    previousWantedTime: undefined,
    isWantedTimeAsap: true,
    hasShownFutureDateWarning: false,
    isShowingOrderPlayback: false,
    dropoffLocation: undefined,
    paymentMethodId: undefined,
    shouldUseCredit: true,
    shouldIncludeUtensils: false,

    selectedTip: 0,
    availableTips: [],

    lineItemIdsBeingUpdated: [],
    rewardIdBeingUpdated: undefined,
    rewardTermsBeingShown: undefined,

    // Store order data from the original event for delayed post-order actions
    orderId: undefined,
    hasEarnedLoyaltyPoints: undefined,
  };
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const isWantedTimeToday = (time?: WantedTime) => {
  const adjustedTime = adjustWantedTime(time);
  const validTime = adjustedTime ? ignoreTimezone(adjustedTime) : null;

  return Boolean(validTime && isToday(validTime));
};

/**
 * Adjusts the tip to be the same percentage, but of the new subtotal.
 */
const getNewTip = (
  previousTip: number,
  previousMaxTipAmount: number,
  availableTips: ReadonlyArray<number | 'CUSTOM'>,
  newLedger?: OrderingLedger,
): number => {
  const previousPercentage = getRoundedPercentageOfValue(
    previousTip,
    previousMaxTipAmount,
  );

  // Pre-defined tips, keep the same percentage of the new subtotal.
  if (availableTips.includes(previousPercentage)) {
    const newMaximumTip = getMaxTipAmount(newLedger);
    return getRoundedValueOfPercentage(previousPercentage, newMaximumTip) || 0;
  }

  // Custom tip over new maximum tip, set it to the max allowed.
  const newMaximumTip = getMaxTipAmount(newLedger);
  if (previousTip > newMaximumTip) {
    return newMaximumTip;
  }

  // Custom tip under new maximum tip, remain as is.
  return previousTip;
};

// ─── Types ──────────────────────────────────────────────────────────────────

export type OrderingContext<PartialLineItem, DeliveryOrderDetail> = ContextFrom<
  ReturnType<typeof createOrderingMachine<PartialLineItem, DeliveryOrderDetail>>
>;

export type CreateOrderingMachineParams = {
  orderSubmissionDelay: number;
  wantedTimesPollingInterval: number;
};
